import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  FormControl,
  FormLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createFilterOptions } from "@mui/material/Autocomplete";
import axios from "axios";
import { Buffer } from "buffer";
import welcomeimage from "../images/logo/welcome.jpg";
import logo from "../images/logo/logo.png";
import { cleanSpecialNaming } from "../HelperFx/common";
import { customers } from "../HelperFx/datalinks";
import useFetch from "../Components/UseFetch";

const REACT_EXPRESS = process.env.REACT_APP_EXPRESS_SERVER;
const REACT_UNAME = process.env.REACT_APP_API_USERNAME;
const REACT_PWD = process.env.REACT_APP_API_PASSWORD;
const REACT_DATA_URL = process.env.REACT_APP_API_DATA_URL;
const REACT_APP_COMPANY_URL = process.env.REACT_APP_COMPANY;
const BackendUname = process.env.REACT_APP_BACKEND_API_USERNAME;
const BackendPwd = process.env.REACT_APP_BACKEND_API_PASSWORD;

const fieldSx = {
  "& .MuiFilledInput-root": {
    backgroundColor: "#f7f7f7",
    borderRadius: "4px",
    "&:before, &:after": { display: "none" },
    "&:hover": { backgroundColor: "#f3f3f3" },
    "&.Mui-focused": { backgroundColor: "#f7f7f7" },
  },
};

const labelSx = {
  fontSize: "0.9rem",
  color: "#8bc34a",
  fontWeight: "600",
  mb: 1,
};

const SignUp = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [errorsMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => {
      if (!option || typeof option !== "object") return "";
      const name = option.name || "";
      const address = option.address ? cleanSpecialNaming(option.address) : "";
      return `${name} ${address}`.toLowerCase();
    },
  });

  const token = Buffer.from(`${REACT_UNAME}:${REACT_PWD}`, "utf8").toString(
    "base64",
  );
  const token2 = Buffer.from(`${BackendUname}:${BackendPwd}`, "utf8").toString(
    "base64",
  );

  const customeruri = `${REACT_DATA_URL}${customers}?$top=20&${REACT_APP_COMPANY_URL}`;
  const { data: customerdata } = useFetch(`${customeruri}`, token);
  const customerList = customerdata?.value || [];

  const [formData, setFormData] = useState({
    userName: "",
    customerNo: "",
    customerName: "",
    email: "",
    phoneNo: "",
    jobTitle: "",
    userfullname: "",
    isAdmin: "USER",
    blocked: true,
  });

  const fetchCompaniesBySearch = async (query) => {
    if (!query || query.length < 2) return;

    const encodedQuery = encodeURIComponent(
      `contains(searchName,'${query.toUpperCase()}')`,
    );
    const searchUri = `${REACT_DATA_URL}${customers}?$filter=${encodedQuery}&$top=20&${REACT_APP_COMPANY_URL}`;

    try {
      setSearching(true);
      const response = await axios.get(searchUri, {
        headers: {
          Authorization: `Basic ${token}`,
        },
        validateStatus: () => true,
      });

      const results = response?.data?.value || [];
      setSearchResults(results);
    } catch (_error) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const showError = (message) => {
    setSuccessMsg("");
    setErrorMsg(message);
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    const username = formData.userName.trim();
    const email = formData.email.trim();

    if (!username) {
      showError("Username is required.");
      return;
    }
    if (username.length > 20) {
      showError("Username must be 20 characters or fewer.");
      return;
    }
    if (!formData.customerNo) {
      showError("Please select a company.");
      return;
    }
    if (!formData.userfullname.trim()) {
      showError("Full name is required.");
      return;
    }
    if (!email) {
      showError("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError("Enter a valid email address.");
      return;
    }
    if (!formData.phoneNo.trim()) {
      showError("Phone number is required.");
      return;
    }
    if (!formData.jobTitle.trim()) {
      showError("Job title is required.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${REACT_EXPRESS}/users`,
        {
          ...formData,
          userName: username,
          email,
        },
        {
          headers: {
            Authorization: `Basic ${token2}`,
          },
        },
      );
      const { success, message } = response.data;
      if (success && message === "user created successfully") {
        setSuccessMsg("Account created successfully! Redirecting to login...");
        setErrorMsg("");
        setOpen(true);
        setTimeout(() => {
          navigate("/signin");
        }, 4000);
      } else {
        showError(message || "Unable to create the account.");
      }
    } catch (error) {
      const data = error.response?.data;
      const apiError = data?.error ?? data?.message ?? data?.errors;

      if (typeof apiError === "string" && apiError.trim()) {
        showError(apiError);
      } else if (apiError && typeof apiError === "object") {
        showError(formatErrorMessage(apiError));
      } else {
        showError("Something went wrong while creating the account.");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatErrorMessage = (msg) => {
    if (Array.isArray(msg)) {
      return msg.map((item) => `• ${item?.message || item}`).join("\n");
    }
    if (typeof msg === "object" && msg !== null) {
      return Object.values(msg)
        .filter(Boolean)
        .map((m) => `• ${m}`)
        .join("\n");
    }
    return msg;
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 2,
          display: { xs: "none", md: "block" },
          position: "relative",
          backgroundImage: `url(${welcomeimage})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(46, 30, 84, 0.45)",
            mixBlendMode: "multiply",
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 4, lg: 8 },
          py: 3,
          overflowY: "auto",
        }}
      >
        <Box sx={{ maxWidth: "400px", width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "left", mb: 2 }}>
            <img
              src={logo}
              alt="Logo"
              style={{ width: "130px", height: "auto" }}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 500, color: "#333", mb: 1.5, fontSize: "1.6rem" }}
          >
            Create Account
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "#555", mb: 4, lineHeight: 1.5 }}
          >
            Register for the Customer Self-Service Portal.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel htmlFor="userName" sx={labelSx}>
                User Name:
              </FormLabel>
              <TextField
                id="userName"
                name="userName"
                variant="filled"
                placeholder="User Name"
                hiddenLabel
                fullWidth
                size="small"
                required
                value={formData.userName}
                onChange={handleChange}
                sx={fieldSx}
                inputProps={{ maxLength: 20 }}
                helperText={`${formData.userName.length}/20 characters`}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel htmlFor="customerName" sx={labelSx}>
                Company:
              </FormLabel>
              <TextField
                id="customerName"
                name="customerName"
                variant="filled"
                placeholder="Select Company"
                hiddenLabel
                fullWidth
                size="small"
                required
                value={
                  selectedCompany?.name ||
                  selectedCompany?.searchName ||
                  formData.customerName ||
                  ""
                }
                onClick={() => setCompanyDialogOpen(true)}
                InputProps={{ readOnly: true }}
                sx={{
                  ...fieldSx,
                  "& .MuiFilledInput-root": {
                    ...fieldSx["& .MuiFilledInput-root"],
                    cursor: "pointer",
                  },
                  "& .MuiInputBase-input": { cursor: "pointer" },
                }}
              />
            </FormControl>

            <TextField
              name="customerNo"
              value={formData.customerNo || ""}
              onChange={handleChange}
              required
              sx={{ display: "none" }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel htmlFor="userfullname" sx={labelSx}>
                User Fullname:
              </FormLabel>
              <TextField
                id="userfullname"
                name="userfullname"
                variant="filled"
                placeholder="User Fullname"
                hiddenLabel
                fullWidth
                size="small"
                required
                value={formData.userfullname}
                onChange={handleChange}
                sx={fieldSx}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel htmlFor="email" sx={labelSx}>
                Email Address:
              </FormLabel>
              <TextField
                id="email"
                name="email"
                type="email"
                variant="filled"
                placeholder="Email Address"
                hiddenLabel
                fullWidth
                size="small"
                required
                value={formData.email}
                onChange={handleChange}
                sx={fieldSx}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel htmlFor="phoneNo" sx={labelSx}>
                Phone Number:
              </FormLabel>
              <TextField
                id="phoneNo"
                name="phoneNo"
                type="tel"
                variant="filled"
                placeholder="Phone Number"
                hiddenLabel
                fullWidth
                size="small"
                required
                value={formData.phoneNo}
                onChange={handleChange}
                sx={fieldSx}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel htmlFor="jobTitle" sx={labelSx}>
                Job Title:
              </FormLabel>
              <TextField
                id="jobTitle"
                name="jobTitle"
                variant="filled"
                placeholder="Job Title"
                hiddenLabel
                fullWidth
                size="small"
                required
                value={formData.jobTitle}
                onChange={handleChange}
                sx={fieldSx}
              />
            </FormControl>

            {(errorsMsg || successMsg) && (
              <Alert
                severity={successMsg ? "success" : "error"}
                sx={{ mb: 2, whiteSpace: "pre-wrap" }}
              >
                {successMsg || formatErrorMessage(errorsMsg)}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.6,
                backgroundColor: "#2e4d44",
                borderRadius: "4px",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#1e332d", boxShadow: "none" },
                textTransform: "none",
                fontSize: "1rem",
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign up"
              )}
            </Button>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Link
                component="button"
                type="button"
                onClick={() => navigate("/signin")}
                variant="body2"
                sx={{
                  color: "#8bc34a",
                  fontWeight: "bold",
                  textDecoration: "none",
                }}
              >
                Already Have An Account, Sign In
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 500, color: "#333" }}>
          Select a Company
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={searchText.length > 2 ? searchResults : customerList}
            loading={searching}
            filterOptions={filterOptions}
            getOptionLabel={(option) => {
              if (!option || typeof option !== "object") return "";
              const name = option.name || "";
              const address = option.address
                ? cleanSpecialNaming(option.address)
                : "";
              return `${name}${address ? ` - ${address}` : ""}`;
            }}
            isOptionEqualToValue={(option, value) => option.no === value?.no}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search Company"
                variant="filled"
                hiddenLabel
                fullWidth
                sx={{ mt: 1, ...fieldSx }}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchText(value);
                  fetchCompaniesBySearch(value);
                }}
              />
            )}
            onChange={(event, value) => {
              if (value) {
                setSelectedCompany(value);
                setFormData((prev) => ({
                  ...prev,
                  customerNo: value.no,
                  customerName: value.name,
                }));
              }
              setCompanyDialogOpen(false);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCompanyDialogOpen(false)}
            sx={{ color: "#2e4d44", textTransform: "none" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        message={
          <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
            {successMsg || formatErrorMessage(errorsMsg)}
          </pre>
        }
        action={
          <IconButton size="small" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default SignUp;
